import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { backendConfig } from '../config/backendConfig';
import KidBackground from '../ui/backgrounds/KidBackground';
import ArtmosphereNavbar from '../components/ArtmosphereNavbar';

// Default feature structure for each plan
const defaultPlanFeatures = {
  'artmocode': {
    artHistoryAppreciation: { enabled: true, limit: 5 },
    artComparison: { enabled: false, limit: 0 },
    styleTransfer: { enabled: true, limit: 3, tutorial: false },
    dailyActivities: { enabled: true, limit: 3 },
    individualArtAppreciation: { enabled: true, limit: 3 },
    coLearningSpace: { enabled: true, limit: 'unlimited' },
    reimagineStudio: { enabled: true, limit: 'unlimited' },
    storyboarding: { enabled: false, limit: 0 }
  },
  'basic': {
    artHistoryAppreciation: { enabled: true, limit: 15 },
    artComparison: { enabled: true, limit: 15 },
    styleTransfer: { enabled: true, limit: 15, tutorial: true },
    dailyActivities: { enabled: true, limit: 'unlimited' },
    individualArtAppreciation: { enabled: true, limit: 10 },
    coLearningSpace: { enabled: true, limit: 'unlimited' },
    reimagineStudio: { enabled: true, limit: 'unlimited' },
    storyboarding: { enabled: false, limit: 0 }
  },
  'pro': {
    artHistoryAppreciation: { enabled: true, limit: 'unlimited' },
    artComparison: { enabled: true, limit: 'unlimited' },
    styleTransfer: { enabled: true, limit: 'unlimited', tutorial: true },
    dailyActivities: { enabled: true, limit: 'unlimited' },
    individualArtAppreciation: { enabled: true, limit: 'unlimited' },
    coLearningSpace: { enabled: true, limit: 'unlimited' },
    reimagineStudio: { enabled: true, limit: 'unlimited' },
    storyboarding: { enabled: true, limit: 'unlimited' }
  }
};

const featureLabels = {
  artHistoryAppreciation: 'Art History (artworks per period)',
  artComparison: 'Art History Compare (included in Art History)',
  styleTransfer: 'Style Transfer (artworks per period)',
  dailyActivities: 'Daily Activities (feedback only)',
  individualArtAppreciation: 'Individual Art Appreciation',
  coLearningSpace: 'Co-Learning Space',
  reimagineStudio: 'Reimagine Studio',
  storyboarding: 'Storyboarding - "Once upon a click"'
};

export default function AdminMembershipDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({
    'artmocode': { 
      price: 'Free', 
      discount: 0, 
      active: true,
      features: defaultPlanFeatures['artmocode']
    },
    'basic': { 
      price: 199, 
      discount: 0, 
      active: true,
      features: defaultPlanFeatures['basic']
    },
    'pro': { 
      price: 399, 
      discount: 0, 
      active: true,
      features: defaultPlanFeatures['pro']
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [membershipConfig, setMembershipConfig] = useState({
    premiumAddonMonthlyPrice: 200,
    currency: 'INR',
    isUpgradeEnabled: true,
  });
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    // Check if user is admin using backend API
    const checkAdmin = async () => {
      if (!user) {
        navigate('/login');
        return;
      }
      
      try {
        console.log('🔍 AdminDashboard - Checking admin status for:', user.uid);
        console.log('🔍 AdminDashboard - Backend URL:', backendConfig.baseUrl);
        
        const idToken = await auth.currentUser?.getIdToken?.();
        
        // Check admin status via backend API (bypasses Firestore security rules)
        const adminCheckRes = await fetch(`${backendConfig.baseUrl}/api/admin/check-admin?uid=${user.uid}`, {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          }
        });
        
        console.log('🔍 AdminDashboard - Admin check response status:', adminCheckRes.status);
        
        if (!adminCheckRes.ok) {
          const errorText = await adminCheckRes.text();
          console.error('❌ AdminDashboard - Admin check failed:', errorText);
          throw new Error(`Failed to check admin status: ${adminCheckRes.status}`);
        }
        
        const adminData = await adminCheckRes.json();
        console.log('🔍 AdminDashboard - Admin check result:', adminData);
        
        if (!adminData.isAdmin) {
          console.log('❌ AdminDashboard - User is not admin, redirecting...');
          navigate('/artist-wall');
          return;
        }
        
        console.log('✅ AdminDashboard - User is admin, loading plans...');
        
        // Load plans from backend API with timeout
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const plansRes = await fetch(`${backendConfig.baseUrl}/api/admin/membership-plans`, {
            method: 'GET',
            headers: { 
              'Content-Type': 'application/json',
              ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
            },
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          console.log('🔍 AdminDashboard - Plans response status:', plansRes.status);
          
          if (plansRes.ok) {
            const plansData = await plansRes.json();
            console.log('🔍 AdminDashboard - Plans data received:', plansData);
            
            if (plansData.plans) {
              const loadedPlans = plansData.plans;
              // Merge with defaults to ensure all features are present
              const mergedPlans = {};
              Object.keys(plans).forEach(planId => {
                mergedPlans[planId] = {
                  ...plans[planId],
                  ...loadedPlans[planId],
                  features: {
                    ...defaultPlanFeatures[planId],
                    ...(loadedPlans[planId]?.features || {})
                  }
                };
              });
              console.log('✅ AdminDashboard - Merged plans:', mergedPlans);
              setPlans(mergedPlans);
            } else {
              console.log('ℹ️ AdminDashboard - No plans found in Firestore, using defaults');
            }
          } else {
            console.warn('⚠️ AdminDashboard - Failed to load plans, using defaults');
          }

          try {
            const cfgRes = await fetch(`${backendConfig.baseUrl}/api/admin/membership-config`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
              },
            });
            if (cfgRes.ok) {
              const cfg = await cfgRes.json();
              setMembershipConfig({
                premiumAddonMonthlyPrice: cfg.premiumAddonMonthlyPrice ?? 200,
                currency: cfg.currency || 'INR',
                isUpgradeEnabled: cfg.isUpgradeEnabled !== false,
              });
            }
          } catch (cfgErr) {
            console.warn('AdminDashboard - membership config load skipped', cfgErr);
          }
        } catch (fetchError) {
          if (fetchError.name === 'AbortError') {
            console.warn('⏱️ AdminDashboard - Plans fetch timed out, using defaults');
          } else {
            console.warn('⚠️ AdminDashboard - Error loading plans:', fetchError, 'using defaults');
          }
        }
      } catch (error) {
        console.error('❌ AdminDashboard - Error checking admin:', error);
        setMessage({ type: 'error', text: `Error loading dashboard: ${error.message}` });
      } finally {
        console.log('✅ AdminDashboard - Setting loading to false');
        setLoading(false);
      }
    };
    
    checkAdmin();
  }, [user, navigate]);

  const handleSave = async () => {
    console.log('💾 AdminDashboard - handleSave called');
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    let timeoutId = null;
    try {
      console.log('💾 AdminDashboard - Saving plans:', plans);
      
      // Create AbortController for timeout
      const controller = new AbortController();
      timeoutId = setTimeout(() => {
        console.log('⏱️ AdminDashboard - Request timeout triggered');
        controller.abort();
      }, 30000); // 30 second timeout
      
      // Save via backend API (which saves to Firestore with admin privileges)
      console.log('💾 AdminDashboard - Sending request to:', `${backendConfig.baseUrl}/api/admin/update-plans`);
      const idToken = await auth.currentUser?.getIdToken?.();
      const res = await fetch(`${backendConfig.baseUrl}/api/admin/update-plans`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({ plans }),
        signal: controller.signal
      });
      
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      console.log('💾 AdminDashboard - Save response status:', res.status);
      console.log('💾 AdminDashboard - Response headers:', Object.fromEntries(res.headers.entries()));
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ AdminDashboard - Save failed, response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || 'Unknown error' };
        }
        throw new Error(errorData.error || `Failed to update plans: ${res.status}`);
      }
      
      const text = await res.text();
      console.log('💾 AdminDashboard - Response text:', text);
      
      let result = {};
      if (text) {
        try {
          result = JSON.parse(text);
        } catch (parseError) {
          console.error('❌ AdminDashboard - JSON parse error:', parseError);
          throw new Error('Invalid response from server');
        }
      }
      
      console.log('✅ AdminDashboard - Save successful:', result);
      setMessage({ type: 'success', text: '✅ Plans updated successfully! Changes are live for all users.' });
      // Keep message visible for 10 seconds
      setTimeout(() => {
        console.log('💾 AdminDashboard - Clearing success message');
        setMessage({ type: '', text: '' });
      }, 10000);
    } catch (error) {
      console.error('❌ AdminDashboard - Error saving plans:', error);
      console.error('❌ AdminDashboard - Error name:', error.name);
      console.error('❌ AdminDashboard - Error message:', error.message);
      if (error.stack) {
        console.error('❌ AdminDashboard - Error stack:', error.stack);
      }
      
      if (error.name === 'AbortError') {
        setMessage({ type: 'error', text: 'Request timed out. Please check your connection and try again.' });
      } else {
        setMessage({ type: 'error', text: error.message || 'Error saving plans. Please try again.' });
      }
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      console.log('💾 AdminDashboard - Finally block executing, setting saving to false');
      setSaving(false);
    }
  };

  const handleSaveMembershipConfig = async () => {
    setSavingConfig(true);
    setMessage({ type: '', text: '' });
    try {
      const idToken = await auth.currentUser?.getIdToken?.();
      const res = await fetch(`${backendConfig.baseUrl}/api/admin/update-membership-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify(membershipConfig),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Save failed');
      }
      setMessage({ type: 'success', text: '✅ Studio premium add-on pricing saved. It will apply to new checkouts immediately.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Could not save membership config.' });
    } finally {
      setSavingConfig(false);
    }
  };

  const updatePlan = (planId, field, value) => {
    setPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [field]: field === 'price' ? (value === '' ? 0 : parseFloat(value)) : value
      }
    }));
  };

  const updateFeature = (planId, featureKey, field, value) => {
    setPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        features: {
          ...prev[planId].features,
          [featureKey]: {
            ...prev[planId].features[featureKey],
            [field]: field === 'limit' && value !== 'unlimited' ? (value === '' ? 0 : parseInt(value) || 0) : value
          }
        }
      }
    }));
  };

  const toggleFeature = (planId, featureKey) => {
    setPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        features: {
          ...prev[planId].features,
          [featureKey]: {
            ...prev[planId].features[featureKey],
            enabled: !prev[planId].features[featureKey].enabled
          }
        }
      }
    }));
  };

  console.log('🎨 AdminDashboard - Rendering dashboard, loading:', loading, 'plans:', plans);
  
  // Safety check - if user is not available, show error
  if (!user) {
    return (
      <KidBackground>
        <ArtmosphereNavbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-red-600">Please log in to access the admin dashboard.</div>
        </div>
      </KidBackground>
    );
  }
  
  // Safety check - ensure plans are initialized
  if (!plans || !plans['artmocode'] || !plans['basic'] || !plans['pro']) {
    console.error('❌ AdminDashboard - Plans not properly initialized:', plans);
    return (
      <KidBackground>
        <ArtmosphereNavbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-red-600">Error: Plans not initialized. Please refresh the page.</div>
        </div>
      </KidBackground>
    );
  }
  
  console.log('✅ AdminDashboard - All checks passed, rendering UI');
  console.log('✅ AdminDashboard - Plans structure:', {
    'artmocode': plans['artmocode'] ? 'exists' : 'missing',
    'basic': plans['basic'] ? 'exists' : 'missing',
    'pro': plans['pro'] ? 'exists' : 'missing'
  });
  
  console.log('🎨 AdminDashboard - About to return JSX');
  
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '20px' }}>
      <ArtmosphereNavbar />
      <div className="min-h-screen p-8" style={{ paddingTop: '100px', position: 'relative', zIndex: 1000 }}>
        {loading && (
          <div className="max-w-7xl mx-auto mb-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Checking permissions and refreshing plans...
            </div>
          </div>
        )}
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl border-2 border-amber-200 p-8" style={{ minHeight: '200px', position: 'relative', zIndex: 1001 }}>
            <h1 className="text-3xl font-bold text-amber-900 mb-2">Membership Plans Admin</h1>
            <p className="text-amber-700 mb-6">Update plan prices, discounts, and feature limits. Changes will reflect immediately on the membership page for all users.</p>
            <p className="text-xs text-amber-600 mb-4">Logged in as: {user?.email} (Admin)</p>

            <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-violet-50 to-fuchsia-50 border-2 border-violet-200">
              <h2 className="text-xl font-bold text-violet-950 mb-2">ArtmoCode → Premium add-on (monthly)</h2>
              <p className="text-sm text-violet-900/90 mb-4">
                Shown after signup when users redeem book codes. The one-time upgrade charge is this monthly rate × (time
                left until the user&apos;s access end date ÷ 30 days), using a standard 30-day month for proration. The app
                reads this from Firestore — do not hardcode prices in the client.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-semibold text-violet-900 mb-1">Premium add-on (per month)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={membershipConfig.premiumAddonMonthlyPrice}
                    onChange={(e) =>
                      setMembershipConfig((c) => ({
                        ...c,
                        premiumAddonMonthlyPrice: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="w-full border border-violet-300 rounded-lg px-3 py-2 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-violet-900 mb-1">Currency</label>
                  <input
                    type="text"
                    value={membershipConfig.currency}
                    onChange={(e) => setMembershipConfig((c) => ({ ...c, currency: e.target.value || 'INR' }))}
                    className="w-full border border-violet-300 rounded-lg px-3 py-2 bg-white"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer pb-2">
                  <input
                    type="checkbox"
                    checked={membershipConfig.isUpgradeEnabled}
                    onChange={(e) => setMembershipConfig((c) => ({ ...c, isUpgradeEnabled: e.target.checked }))}
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-semibold text-violet-900">Allow premium upgrade checkout</span>
                </label>
              </div>
              <button
                type="button"
                onClick={handleSaveMembershipConfig}
                disabled={savingConfig}
                className="mt-4 px-6 py-2 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 disabled:opacity-50"
              >
                {savingConfig ? 'Saving…' : 'Save add-on pricing'}
              </button>
            </div>
            
            <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <h3 className="text-sm font-bold text-amber-900 mb-2">Where to change quotas</h3>
              <p className="text-xs text-amber-800 mb-2">Below, under each plan (Artmo Code, Basic, Pro), the <strong className="text-amber-900">“Quota (per period)”</strong> field is highlighted — that is the value shown in the app’s “Quota available” modal and used for enforcement. Change that dropdown and click Save.</p>
              <ul className="text-xs text-amber-800 space-y-1">
                <li><strong>Art History (artworks)</strong> — Learn Art History flow (including Compare): one count per artwork explored (Start analysis).</li>
                <li><strong>Style Transfer</strong> — Ways of Seeing: up to 3 style transfers per artwork; tutorial for those styles is included. Admin quota controls how many artworks a user can unlock.</li>
                <li><strong>Daily Activities</strong> — Artmosphere feedback submissions only (not attempting activities).</li>
                <li><strong>Individual Art Appreciation</strong> — User-uploaded artwork analysis (Start Analysis on your art).</li>
                <li><strong>Co-Learning Space</strong> — No quota (unlimited when enabled).</li>
                <li><strong>Reimagine Studio</strong> — No quota (unlimited when enabled).</li>
                <li><strong>Storyboarding</strong> — Pro-only; no numeric limit when enabled.</li>
              </ul>
            </div>
            
            {message.text && (
              <div className={`mb-6 p-6 rounded-xl font-bold shadow-2xl ${
                message.type === 'success' 
                  ? 'bg-gradient-to-r from-emerald-400 to-green-500 border-4 border-emerald-600 text-white' 
                  : 'bg-gradient-to-r from-red-400 to-red-600 border-4 border-red-600 text-white'
              }`} style={{ fontSize: '18px', zIndex: 10000, position: 'relative' }}>
                <div className="flex items-center gap-3">
                  {message.type === 'success' ? (
                    <span className="text-3xl animate-bounce">✅</span>
                  ) : (
                    <span className="text-3xl">❌</span>
                  )}
                  <span className="text-lg">{message.text}</span>
                </div>
              </div>
            )}

            <div className="space-y-8">
              {/* Artmo Code Plan */}
              <div className="border-2 border-amber-200 rounded-xl p-6 bg-gradient-to-br from-amber-50 to-orange-50">
                <h2 className="text-2xl font-bold text-amber-900 mb-6">Artmo Code Plan (30 days)</h2>
                
                {/* Pricing Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-amber-800 mb-2">Price</label>
                    <input
                      type="text"
                      value={plans['artmocode']?.price || 'Free'}
                      onChange={(e) => updatePlan('artmocode', 'price', e.target.value)}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                      disabled
                    />
                    <p className="text-xs text-amber-600 mt-1">Artmo Code unlock is always free</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-amber-800 mb-2">Discount (%)</label>
                    <input
                      type="number"
                      value={plans['artmocode']?.discount ?? 0}
                      onChange={(e) => updatePlan('artmocode', 'discount', e.target.value)}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={plans['artmocode']?.active ?? true}
                        onChange={(e) => updatePlan('artmocode', 'active', e.target.checked)}
                        className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                      />
                      <span className="text-sm font-semibold text-amber-800">Active</span>
                    </label>
                  </div>
                </div>

                {/* Features Section - these limits drive "Quota available" + enforcement */}
                <div className="mt-6">
                  <h3 className="text-lg font-bold text-amber-900 mb-1">Feature Limits & Access</h3>
                  <p className="text-xs text-amber-700 mb-4">✓ = access on/off. <strong className="text-amber-800">Quota</strong> = number shown in “Quota available” and used for enforcement (per billing period).</p>
                  <div className="space-y-4">
                    {Object.entries(plans['artmocode']?.features || {}).map(([featureKey, feature]) => {
                      if (!feature || typeof feature !== 'object') return null;
                      return (
                      <div key={featureKey} className="bg-white/80 rounded-lg p-4 border border-amber-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={feature?.enabled ?? false}
                              onChange={() => toggleFeature('artmocode', featureKey)}
                              className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                            />
                            <label className="font-semibold text-amber-900">{featureLabels[featureKey] || featureKey}</label>
                          </div>
                        </div>
                        {feature?.enabled && (
                          <div className="ml-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="ring-2 ring-amber-400/60 rounded-lg px-3 py-2 bg-amber-50/80">
                              <label className="block text-xs font-bold text-amber-800 mb-1 uppercase tracking-wide">Quota (per period)</label>
                              <select
                                value={feature?.limit || 'unlimited'}
                                onChange={(e) => updateFeature('artmocode', featureKey, 'limit', e.target.value)}
                                className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              >
                                <option value="0">0 (Disabled)</option>
                                <option value="1">1</option>
                                <option value="3">3</option>
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="unlimited">Unlimited</option>
                              </select>
                            </div>
                            {featureKey === 'styleTransfer' && (
                              <div className="flex items-center">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={feature.tutorial || false}
                                    onChange={(e) => updateFeature('artmocode', featureKey, 'tutorial', e.target.checked)}
                                    className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                                  />
                                  <span className="text-xs text-amber-700">Tutorial Access</span>
                                </label>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Basic Plan */}
              <div className="border-2 border-amber-200 rounded-xl p-6 bg-gradient-to-br from-amber-50 to-orange-50">
                <h2 className="text-2xl font-bold text-amber-900 mb-6">Basic Plan</h2>
                
                {/* Pricing Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-amber-800 mb-2">Price (₹/month)</label>
                    <input
                      type="number"
                      value={plans['basic']?.price ?? 199}
                      onChange={(e) => updatePlan('basic', 'price', e.target.value)}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-amber-800 mb-2">Discount (%)</label>
                    <input
                      type="number"
                      value={plans['basic']?.discount ?? 0}
                      onChange={(e) => updatePlan('basic', 'discount', e.target.value)}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                      max="100"
                    />
                    {(plans['basic']?.discount ?? 0) > 0 && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Final price: ₹{(((plans['basic']?.price ?? 199) * (1 - (plans['basic']?.discount ?? 0) / 100)).toFixed(2))}/month
                      </p>
                    )}
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={plans['basic']?.active ?? true}
                        onChange={(e) => updatePlan('basic', 'active', e.target.checked)}
                        className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                      />
                      <span className="text-sm font-semibold text-amber-800">Active</span>
                    </label>
                  </div>
                </div>

                {/* Features Section - these limits drive "Quota available" + enforcement */}
                <div className="mt-6">
                  <h3 className="text-lg font-bold text-amber-900 mb-1">Feature Limits & Access</h3>
                  <p className="text-xs text-amber-700 mb-4">✓ = access on/off. <strong className="text-amber-800">Quota</strong> = number shown in “Quota available” and used for enforcement (per billing period).</p>
                  <div className="space-y-4">
                    {Object.entries(plans['basic']?.features || {}).map(([featureKey, feature]) => {
                      if (!feature || typeof feature !== 'object') return null;
                      return (
                      <div key={featureKey} className="bg-white/80 rounded-lg p-4 border border-amber-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={feature?.enabled ?? false}
                              onChange={() => toggleFeature('basic', featureKey)}
                              className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                            />
                            <label className="font-semibold text-amber-900">{featureLabels[featureKey] || featureKey}</label>
                          </div>
                        </div>
                        {feature.enabled && (
                          <div className="ml-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="ring-2 ring-amber-400/60 rounded-lg px-3 py-2 bg-amber-50/80">
                              <label className="block text-xs font-bold text-amber-800 mb-1 uppercase tracking-wide">Quota (per period)</label>
                              <select
                                value={feature?.limit || 'unlimited'}
                                onChange={(e) => updateFeature('basic', featureKey, 'limit', e.target.value)}
                                className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              >
                                <option value="0">0 (Disabled)</option>
                                <option value="1">1</option>
                                <option value="3">3</option>
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="unlimited">Unlimited</option>
                              </select>
                            </div>
                            {featureKey === 'styleTransfer' && (
                              <div className="flex items-center">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={feature.tutorial || false}
                                    onChange={(e) => updateFeature('basic', featureKey, 'tutorial', e.target.checked)}
                                    className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                                  />
                                  <span className="text-xs text-amber-700">Tutorial Access</span>
                                </label>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Pro Plan */}
              <div className="border-2 border-amber-200 rounded-xl p-6 bg-gradient-to-br from-amber-50 to-orange-50">
                <h2 className="text-2xl font-bold text-amber-900 mb-6">Pro Plan</h2>
                
                {/* Pricing Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-amber-800 mb-2">Price (₹/month)</label>
                    <input
                      type="number"
                      value={plans['pro']?.price ?? 399}
                      onChange={(e) => updatePlan('pro', 'price', e.target.value)}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-amber-800 mb-2">Discount (%)</label>
                    <input
                      type="number"
                      value={plans['pro']?.discount ?? 0}
                      onChange={(e) => updatePlan('pro', 'discount', e.target.value)}
                      className="w-full border border-amber-300 rounded-lg px-3 py-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                      max="100"
                    />
                    {(plans['pro']?.discount ?? 0) > 0 && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Final price: ₹{(((plans['pro']?.price ?? 399) * (1 - (plans['pro']?.discount ?? 0) / 100)).toFixed(2))}/month
                      </p>
                    )}
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={plans['pro']?.active ?? true}
                        onChange={(e) => updatePlan('pro', 'active', e.target.checked)}
                        className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                      />
                      <span className="text-sm font-semibold text-amber-800">Active</span>
                    </label>
                  </div>
                </div>

                {/* Features Section - these limits drive "Quota available" + enforcement */}
                <div className="mt-6">
                  <h3 className="text-lg font-bold text-amber-900 mb-1">Feature Limits & Access</h3>
                  <p className="text-xs text-amber-700 mb-4">✓ = access on/off. <strong className="text-amber-800">Quota</strong> = number shown in “Quota available” and used for enforcement (per billing period).</p>
                  <div className="space-y-4">
                    {Object.entries(plans['pro']?.features || {}).map(([featureKey, feature]) => {
                      if (!feature || typeof feature !== 'object') return null;
                      return (
                      <div key={featureKey} className="bg-white/80 rounded-lg p-4 border border-amber-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={feature?.enabled ?? false}
                              onChange={() => toggleFeature('pro', featureKey)}
                              className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                            />
                            <label className="font-semibold text-amber-900">{featureLabels[featureKey] || featureKey}</label>
                          </div>
                        </div>
                        {feature.enabled && (
                          <div className="ml-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="ring-2 ring-amber-400/60 rounded-lg px-3 py-2 bg-amber-50/80">
                              <label className="block text-xs font-bold text-amber-800 mb-1 uppercase tracking-wide">Quota (per period)</label>
                              <select
                                value={feature?.limit || 'unlimited'}
                                onChange={(e) => updateFeature('pro', featureKey, 'limit', e.target.value)}
                                className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              >
                                <option value="0">0 (Disabled)</option>
                                <option value="1">1</option>
                                <option value="3">3</option>
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="unlimited">Unlimited</option>
                              </select>
                            </div>
                            {featureKey === 'styleTransfer' && (
                              <div className="flex items-center">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={feature.tutorial || false}
                                    onChange={(e) => updateFeature('pro', featureKey, 'tutorial', e.target.checked)}
                                    className="w-5 h-5 text-amber-600 border-amber-300 rounded"
                                  />
                                  <span className="text-xs text-amber-700">Tutorial Access</span>
                                </label>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-4">
              <button
                onClick={() => navigate('/artist-wall')}
                className="px-6 py-3 rounded-xl border-2 border-amber-300 text-amber-800 bg-white hover:bg-amber-50 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-6 py-3 rounded-xl font-semibold text-white transition-all ${
                  saving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg'
                }`}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
}
